  import React from 'react';
  import { 
    FormControl, 
    InputLabel, 
    Select, 
    MenuItem, 
    Typography, 
    Box, 
    Paper, 
    FormHelperText,
    TextField,
    ListSubheader
  } from '@mui/material';
  import Autocomplete from '@mui/material/Autocomplete';
  import { MapPin, Briefcase } from 'lucide-react';

  const SearchFilters = ({ filters, handleFilterChange }) => {
    // Countries organized by region with job board support information
    const locations = [
      { region: "Popular Locations", countries: [
        { name: "United States", supported: ["LinkedIn", "Indeed", "Glassdoor", "ZipRecruiter", "Google"] },
        { name: "India", supported: ["LinkedIn", "Indeed", "Glassdoor", "Google"] },
        { name: "Remote", supported: ["LinkedIn", "Indeed", "Google"] },
        { name: "United Kingdom", supported: ["LinkedIn", "Indeed", "Glassdoor", "Google"] },
        { name: "Canada", supported: ["LinkedIn", "Indeed", "Glassdoor", "ZipRecruiter", "Google"] },
        { name: "Australia", supported: ["LinkedIn", "Indeed", "Glassdoor", "Google"] },
        { name: "Germany", supported: ["LinkedIn", "Indeed", "Glassdoor", "Google"] },
        { name: "Singapore", supported: ["LinkedIn", "Indeed", "Glassdoor", "Google"] },
      ]},
      { region: "North America", countries: [
        { name: "United States", supported: ["LinkedIn", "Indeed", "Glassdoor", "ZipRecruiter", "Google"] },
        { name: "Canada", supported: ["LinkedIn", "Indeed", "Glassdoor", "ZipRecruiter", "Google"] },
        { name: "Mexico", supported: ["LinkedIn", "Indeed", "Glassdoor", "Google"] },
        { name: "Costa Rica", supported: ["LinkedIn", "Indeed", "Google"] },
        { name: "Panama", supported: ["LinkedIn", "Indeed", "Google"] },
      ]},
      { region: "South America", countries: [
        { name: "Argentina", supported: ["LinkedIn", "Indeed", "Google"] },
        { name: "Brazil", supported: ["LinkedIn", "Indeed", "Glassdoor", "Google"] },
        { name: "Chile", supported: ["LinkedIn", "Indeed", "Google"] },
        { name: "Colombia", supported: ["LinkedIn", "Indeed", "Google"] },
        { name: "Ecuador", supported: ["LinkedIn", "Indeed", "Google"] },
        { name: "Peru", supported: ["LinkedIn", "Indeed", "Google"] },
        { name: "Uruguay", supported: ["LinkedIn", "Indeed", "Google"] },
        { name: "Venezuela", supported: ["LinkedIn", "Indeed", "Google"] },
      ]},
      { region: "Europe", countries: [
        { name: "United Kingdom", supported: ["LinkedIn", "Indeed", "Glassdoor", "Google"] },
        { name: "Austria", supported: ["LinkedIn", "Indeed", "Glassdoor", "Google"] },
        { name: "Belgium", supported: ["LinkedIn", "Indeed", "Glassdoor", "Google"] },
        { name: "Czech Republic", supported: ["LinkedIn", "Indeed", "Google"] },
        { name: "Denmark", supported: ["LinkedIn", "Indeed", "Google"] },
        { name: "Finland", supported: ["LinkedIn", "Indeed", "Google"] },
        { name: "France", supported: ["LinkedIn", "Indeed", "Glassdoor", "Google"] },
        { name: "Germany", supported: ["LinkedIn", "Indeed", "Glassdoor", "Google"] },
        { name: "Greece", supported: ["LinkedIn", "Indeed", "Google"] },
        { name: "Hungary", supported: ["LinkedIn", "Indeed", "Google"] },
        { name: "Ireland", supported: ["LinkedIn", "Indeed", "Glassdoor", "Google"] },
        { name: "Italy", supported: ["LinkedIn", "Indeed", "Glassdoor", "Google"] },
        { name: "Luxembourg", supported: ["LinkedIn", "Indeed", "Google"] },
        { name: "Netherlands", supported: ["LinkedIn", "Indeed", "Glassdoor", "Google"] },
        { name: "Norway", supported: ["LinkedIn", "Indeed", "Google"] },
        { name: "Poland", supported: ["LinkedIn", "Indeed", "Google"] },
        { name: "Portugal", supported: ["LinkedIn", "Indeed", "Google"] },
        { name: "Romania", supported: ["LinkedIn", "Indeed", "Google"] },
        { name: "Spain", supported: ["LinkedIn", "Indeed", "Glassdoor", "Google"] },
        { name: "Sweden", supported: ["LinkedIn", "Indeed", "Google"] },
        { name: "Switzerland", supported: ["LinkedIn", "Indeed", "Glassdoor", "Google"] },
        { name: "Ukraine", supported: ["LinkedIn", "Indeed", "Google"] },
      ]},
      { region: "Asia & Pacific", countries: [
        { name: "Australia", supported: ["LinkedIn", "Indeed", "Glassdoor", "Google"] },
        { name: "China", supported: ["LinkedIn", "Indeed", "Google"] },
        { name: "Hong Kong", supported: ["LinkedIn", "Indeed", "Glassdoor", "Google"] },
        { name: "India", supported: ["LinkedIn", "Indeed", "Glassdoor", "Google"] },
        { name: "Indonesia", supported: ["LinkedIn", "Indeed", "Google"] },
        { name: "Japan", supported: ["LinkedIn", "Indeed", "Google"] },
        { name: "Malaysia", supported: ["LinkedIn", "Indeed", "Google"] },
        { name: "New Zealand", supported: ["LinkedIn", "Indeed", "Glassdoor", "Google"] },
        { name: "Pakistan", supported: ["LinkedIn", "Indeed", "Google"] },
        { name: "Philippines", supported: ["LinkedIn", "Indeed", "Google"] },
        { name: "Singapore", supported: ["LinkedIn", "Indeed", "Glassdoor", "Google"] },
        { name: "South Korea", supported: ["LinkedIn", "Indeed", "Google"] },
        { name: "Taiwan", supported: ["LinkedIn", "Indeed", "Google"] },
        { name: "Thailand", supported: ["LinkedIn", "Indeed", "Google"] },
        { name: "Vietnam", supported: ["LinkedIn", "Indeed", "Glassdoor", "Google"] },
      ]},
      { region: "Middle East & Africa", countries: [
        { name: "Bahrain", supported: ["LinkedIn", "Indeed", "Google", "Bayt"] },
        { name: "Egypt", supported: ["LinkedIn", "Indeed", "Google", "Bayt"] },
        { name: "Israel", supported: ["LinkedIn", "Indeed", "Google"] },
        { name: "Kuwait", supported: ["LinkedIn", "Indeed", "Google", "Bayt"] },
        { name: "Morocco", supported: ["LinkedIn", "Indeed", "Google", "Bayt"] },
        { name: "Nigeria", supported: ["LinkedIn", "Indeed", "Google"] },
        { name: "Oman", supported: ["LinkedIn", "Indeed", "Google", "Bayt"] },
        { name: "Qatar", supported: ["LinkedIn", "Indeed", "Google", "Bayt"] },
        { name: "Saudi Arabia", supported: ["LinkedIn", "Indeed", "Google", "Bayt"] },
        { name: "South Africa", supported: ["LinkedIn", "Indeed", "Google"] },
        { name: "Turkey", supported: ["LinkedIn", "Indeed", "Google"] },
        { name: "United Arab Emirates", supported: ["LinkedIn", "Indeed", "Google", "Bayt"] },
      ]},
    ];

    // Common job titles moved into the component
    const popularJobTitles = [
      'Software Engineer',
      'Data Scientist',
      'Product Manager',
      'UX Designer',
      'Marketing Manager',
      'DevOps Engineer',
      'Frontend Developer'
    ];

    // Handle job title change for Autocomplete
    const handleJobTitleChange = (event, newValue) => {
      // Simulate an event object for handleFilterChange
      handleFilterChange({
        target: {
          name: 'job_title',
          value: newValue || ''
        }
      });
    };

    // Helper to render country MenuItem with job board tags
    const renderCountryMenuItem = (country) => (
      <MenuItem key={country.name} value={country.name}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Typography>{country.name}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            {country.supported.join(' · ')}
          </Typography>
        </Box>
      </MenuItem>
    );

    return (
      <Paper elevation={2} sx={{ p: 1.5, mb: 2, maxWidth: "400px", mx: "auto" }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: "medium" }}>
          Set Your Job Search Filters
        </Typography>
        
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="location-label" sx={{ fontSize: "0.875rem" }}>
              Location
            </InputLabel>
            <Select
              labelId="location-label"
              name="location"
              value={filters.location}
              onChange={handleFilterChange}
              label="Location"
              startAdornment={<MapPin size={14} style={{ marginRight: 6 }} />}
              sx={{ '& .MuiListSubheader-root': { fontWeight: 'bold', bgcolor: '#f5f5f5' } }}
            >
              {locations.map((regionGroup) => [
                <ListSubheader key={regionGroup.region}>{regionGroup.region}</ListSubheader>,
                ...regionGroup.countries.map(renderCountryMenuItem)
              ])}
            </Select>
            <FormHelperText sx={{ fontSize: "0.7rem", mt: 0.5 }}>
              Job boards vary by country - select for best results
            </FormHelperText>
          </FormControl>

          <FormControl fullWidth size="small">
            <Autocomplete
              freeSolo
              id="job-title-autocomplete"
              options={popularJobTitles}
              value={filters.job_title}
              onChange={handleJobTitleChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Job Title (Optional)"
                  name="job_title"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <Briefcase size={14} style={{ marginRight: 6 }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    autoComplete: "off"
                  }}
                />
              )}
            />
            <FormHelperText sx={{ fontSize: "0.7rem", mt: 0.5 }}>
              Select from the list or type your own job title
            </FormHelperText>
          </FormControl>
        </Box>
      </Paper>
    );
  };

  export default SearchFilters;